import { Dialog } from "@components/ui/Dialog";
import { useToast } from "@lib/hooks/useToast";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Calendar, Copy, Pencil, Trash2, User } from "lucide-react";
import { useState } from "react";
import { ROUTES } from "../../config/constants";
import { useDeleteTicket } from "../../hooks/useTicketMutations";
import type { Ticket } from "../../types/ticket.types";
import { copyText, getInitials } from "../../utils";
import { TicketCategoryBadge } from "../shared/TicketCategoryBadge";
import { TicketStatusBadge } from "../shared/TicketStatusBadge";
import { TicketTransitionActions } from "../shared/TicketTransitionActions";

interface TicketHeaderProps {
  ticket: Ticket;
}

function getCreatorName(ticket: Ticket): string {
  if (ticket.creator) {
    return (
      [ticket.creator.prenom, ticket.creator.nom].filter(Boolean).join(" ") ||
      `#${ticket.creatorId}`
    );
  }
  return `#${ticket.creatorId}`;
}

export function TicketHeader({ ticket }: TicketHeaderProps) {
  const navigate = useNavigate();
  const deleteTicket = useDeleteTicket();
  const toast = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleDelete = () => {
    deleteTicket.mutate(ticket.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        navigate({ to: ROUTES.TICKETS.LIST });
      },
    });
  };

  return (
    <div className="space-y-4">
      {/* Back */}
      <Link to={ROUTES.TICKETS.LIST} className="btn btn-ghost btn-sm gap-1.5">
        <ArrowLeft className="w-4 h-4" />
        Retour à la liste
      </Link>

      {/* Title row + actions */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold leading-snug">{ticket.title}</h1>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <div className="flex items-center gap-1">
              <span className="font-mono text-xs text-base-content/50 bg-base-200 border border-base-300 rounded px-2 py-0.5">
                TK-{String(ticket.id).slice(0, 8)}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-xs btn-square focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:outline-none"
                title="Copier l'ID"
                onClick={() =>
                  copyText(`TK-${String(ticket.id).slice(0, 8)}`).then(() =>
                    toast.success("ID copié", { dedupeKey: "copy", duration: 1500 }),
                  )
                }
              >
                <Copy className="w-3 h-3" />
              </button>
            </div>
            <TicketStatusBadge status={ticket.status} size="md" />
            <TicketCategoryBadge category={ticket.category} size="md" />
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 mt-1">
          <Link
            to={ROUTES.TICKETS.EDIT}
            params={{ id: String(ticket.id) }}
            className="btn btn-outline btn-sm gap-1.5"
            title="Modifier"
          >
            <Pencil className="w-4 h-4" />
            Modifier
          </Link>
          <button
            type="button"
            className="btn btn-ghost btn-sm gap-1.5 text-error hover:bg-error/10"
            title="Supprimer"
            onClick={() => setConfirmOpen(true)}
            disabled={deleteTicket.isPending}
          >
            {deleteTicket.isPending ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </button>
          <TicketTransitionActions ticketId={ticket.id} currentStatus={ticket.status} />
        </div>
      </div>

      {/* Info grid — semantic definition list */}
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-base-300 rounded-lg overflow-hidden text-sm">
        <div className="bg-base-200 p-3 space-y-1">
          <dt className="flex items-center gap-1.5 text-xs font-medium text-base-content/50">
            <User className="w-3 h-3" />
            Créé par
          </dt>
          <dd className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-gradient-to-br from-primary/60 to-primary/30 flex items-center justify-center text-[11px] font-semibold text-white shrink-0">
              {getInitials([ticket.creator?.prenom, ticket.creator?.nom].filter(Boolean).join(" "))}
            </span>
            <span className="font-medium">{getCreatorName(ticket)}</span>
          </dd>
        </div>

        <div className="bg-base-200 p-3 space-y-1">
          <dt className="flex items-center gap-1.5 text-xs font-medium text-base-content/50">
            <Calendar className="w-3 h-3" />
            Créé le
          </dt>
          <dd className="font-medium">
            <time dateTime={ticket.createdAt}>
              {new Date(ticket.createdAt).toLocaleDateString("fr-FR")}
            </time>
          </dd>
        </div>
      </dl>

      {/* Delete confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Supprimer le ticket"
        size="sm"
        footer={
          <div className="flex gap-2">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setConfirmOpen(false)}
            >
              Annuler
            </button>
            <button
              type="button"
              className="btn btn-error btn-sm"
              onClick={handleDelete}
              disabled={deleteTicket.isPending}
            >
              {deleteTicket.isPending ? (
                <span className="loading loading-spinner loading-xs" />
              ) : (
                "Supprimer"
              )}
            </button>
          </div>
        }
      >
        <p className="text-sm text-base-content/70">
          Supprimer définitivement ce ticket ? Cette action est irréversible.
        </p>
      </Dialog>
    </div>
  );
}
